// ── 콜 목록 보조 컴포넌트 (CallList.jsx에서 분리) ────────────────────────────
//   CopyMeetingButton — 미팅 내용 복사 버튼 (CopyAddressButton 패턴)
//   ContractResultChip — 계약결과 chip + 상세 텍스트

import React, { useState } from 'react';
import { formatItemsDisplay } from '../../../utils/contractFormat';
import { dayOfWeekUTC } from '../../../utils/date';
import {
  callResultContractedStyle, callResultCancelledStyle, contractDetailTextStyle, callResultFeedbackStyle,
} from './listChipStyles';

/** 요일 표기 (한국어) */
const DAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 미팅 내용 복사 버튼 — CopyAddressButton과 동일한 UX (2초간 복사됨 표시)
 * @param {object} row       - 콜 행 데이터
 * @param {boolean} iconOnly - true 이면 아이콘만 표시 (텍스트 숨김, 좁은 공간용)
 */
export function CopyMeetingButton({ row, iconOnly = false }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    // 방문일자에 요일 추가
    // dayOfWeekUTC: utils/date.js 사용 (new Date 직접 사용 금지)
    let visitDateStr = row.visit_date || '-';
    if (row.visit_date) {
      visitDateStr = `${row.visit_date}(${DAY_KO[dayOfWeekUTC(row.visit_date)]})`;
    }
    const text = [
      row.ticket_type || '-',
      `방문일자 : ${visitDateStr}`,
      `상호 : ${row.company_name || '-'}`,
      `주소 : ${row.address || '-'}`,
      `미팅시간 : ${row.visit_time || '-'}`,
      `전화번호 : ${row.phone || '-'}`,
      `휴대폰 : ${row.mobile_phone || '-'}`,
      `대표자 : ${row.ceo_name || '-'}`,
      `내용 : ${row.content || '-'}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* 클립보드 접근 실패 무시 */ }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="미팅 내용 복사"
      style={{
        background: 'none',
        border: `1px solid ${copied ? '#86efac' : 'var(--border)'}`,
        borderRadius: 6,
        // iconOnly 시 패딩 축소 (수정·삭제 버튼과 나란히 놓일 때 공간 절약)
        padding: iconOnly ? '3px 5px' : '3px 8px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: iconOnly ? 0 : 4,
        fontSize: 12,
        fontWeight: 500,
        color: copied ? '#16a34a' : 'var(--text-muted)',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {copied ? (
        <>
          {/* 체크 아이콘 */}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {/* iconOnly=false 일 때만 텍스트 표시 */}
          {!iconOnly && '복사됨'}
        </>
      ) : (
        <>
          {/* 복사 아이콘 */}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          {!iconOnly && '복사'}
        </>
      )}
    </button>
  );
}

/** 계약 결과 chip + 날짜/상품 상세 텍스트 */
export function ContractResultChip({ row }) {
  if (!row.is_contracted) return null;
  /* contract_items 배열 기반으로 계약 상품 표시 */
  const disp = formatItemsDisplay(row.contract_items);
  return (
    <>
      {row.contract_status === 'cancelled'
        ? <span style={callResultCancelledStyle}>계약취소</span>
        : <span style={callResultContractedStyle}>계약완료</span>
      }
      {row.contract_status !== 'cancelled' && (row.contract_date || disp !== '-') && (
        <span style={contractDetailTextStyle}>
          {[row.contract_date, disp !== '-' ? disp : null].filter(Boolean).join(' · ')}
        </span>
      )}
    </>
  );
}

/**
 * 결과(피드백) 칩 — onEdit 가 있으면 ✎ 를 붙여 눌러서 고치기·지우기 창을 연다(2026-11-03, callRegisterFeedbackActions.js).
 *   누가 고칠 수 있는지는 창이 열릴 때 서버가 정한다(못 고치면 창이 흐리게 막힘).
 */
export function FeedbackResultChip({ row, onEdit }) {
  if (!onEdit) return <span style={callResultFeedbackStyle}>{row.feedback}</span>;
  return (
    <button type="button" title="결과 고치기·지우기" onClick={() => onEdit(row)}
      style={{ ...callResultFeedbackStyle, border: 'none', cursor: 'pointer', font: 'inherit', fontSize: callResultFeedbackStyle.fontSize, fontWeight: callResultFeedbackStyle.fontWeight, gap: 3 }}>
      {row.feedback}<span aria-hidden="true">✎</span>
    </button>
  );
}
